import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import PlainSearchInput, {ClearType} from 'ui-library/PlainSearchInput';
import {InputChangeEvent} from 'util/Types';

import type {PredictionBatchSearchFilters} from './usePredictionBatchSearch';

const DEBOUNCE_MS = 300;

interface PredictionBatchFiltersProps {
  filters: PredictionBatchSearchFilters;
  onFiltersChange: (filters: PredictionBatchSearchFilters) => void;
}

export default function PredictionBatchFilters({
  filters,
  onFiltersChange,
}: PredictionBatchFiltersProps): JSX.Element {
  const [queryText, setQueryText] = React.useState(filters.query);

  const filtersRef = React.useRef(filters);
  filtersRef.current = filters;
  const onFiltersChangeRef = React.useRef(onFiltersChange);
  onFiltersChangeRef.current = onFiltersChange;

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (queryText !== filtersRef.current.query) {
        onFiltersChangeRef.current({...filtersRef.current, query: queryText});
      }
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [queryText]);

  React.useEffect(() => {
    setQueryText(filters.query);
  }, [filters.query]);

  const handleQueryChange = React.useCallback((e: InputChangeEvent) => {
    setQueryText(e.target.value);
  }, []);

  const handleQueryClear = React.useCallback(() => {
    setQueryText('');
    onFiltersChange({...filters, query: ''});
  }, [filters, onFiltersChange]);

  return (
    <div
      className={css(styles.filtersRow)}
      {...getTestProps({component: 'PredictionBatchFilters', element: 'root'})}
    >
      <PlainSearchInput
        className={css(styles.searchInput)}
        clearType={ClearType.X}
        onChange={handleQueryChange}
        onEscape={handleQueryClear}
        placeholder="Search runs..."
        value={queryText}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  filtersRow: {
    display: 'flex',
    alignItems: 'center',
  },
  searchInput: {
    marginRight: 10,
    marginTop: 6,
    marginBottom: 6,
    width: 300,
    verticalAlign: 'middle',
  },
});
